import { useEffect, useState } from 'react';
import { FileText, AlertCircle, FileSpreadsheet } from 'lucide-react';
import { PDFViewer } from '@/components/ui/PDFViewer';

interface DocumentViewerProps {
  file: File | null;
  className?: string;
}

export function DocumentViewer({ file, className = '' }: DocumentViewerProps) {
  const [pdfBlobUrl, setPdfBlobUrl] = useState<string | null>(null);

  useEffect(() => {
    // Create blob URL for PDFs
    if (file && file.type === 'application/pdf') {
      const url = URL.createObjectURL(file);
      setPdfBlobUrl(url);

      // Cleanup function to revoke the blob URL
      return () => {
        URL.revokeObjectURL(url);
        setPdfBlobUrl(null);
      };
    } else {
      setPdfBlobUrl(null);
    }
  }, [file]);

  if (!file) {
    return (
      <div className={`flex flex-col items-center justify-center p-8 text-muted-foreground ${className}`}>
        <FileText className="w-12 h-12 mb-2" />
        <p className="text-sm">No file uploaded</p>
      </div>
    );
  }

  // Determine file type
  const isPdf = file.type === 'application/pdf';
  const isXlsx =
    file.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
    file.type === 'application/vnd.ms-excel' ||
    file.name.endsWith('.xlsx') ||
    file.name.endsWith('.xls');

  // Render appropriate viewer
  if (isPdf && pdfBlobUrl) {
    return <PDFViewer url={pdfBlobUrl} className={className} />;
  }

  if (isXlsx) {
    return (
      <div className={`flex flex-col items-center justify-center p-8 text-muted-foreground ${className}`}>
        <FileSpreadsheet className="w-12 h-12 mb-2" />
        <p className="text-sm">XLSX preview</p>
        <p className="text-sm">coming soon.</p>
      </div>
    );
  }

  // Unsupported file type
  return (
    <div className={`flex flex-col items-center justify-center p-8 text-muted-foreground ${className}`}>
      <AlertCircle className="w-12 h-12 mb-2" />
      <p className="text-sm">Unsupported file type</p>
      <p className="text-xs mt-1">Only PDF and Excel (.xlsx) files are supported</p>
    </div>
  );
}
